import { useTranslation } from 'react-i18next'
import type { ResourceItem } from '../types'

interface ResourceDetailProps {
  readonly item: ResourceItem
  readonly onClose: () => void
  readonly onUseInGeneration: (item: ResourceItem) => void
}

export function ResourceDetail({ item, onClose, onUseInGeneration }: ResourceDetailProps) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onClose}
          className="text-sm text-tg-link transition-opacity hover:opacity-80 active:opacity-60"
        >
          {t('common.back')}
        </button>
      </div>

      {/* Name */}
      <h2 className="text-lg font-semibold text-tg-text">
        {item.name}
      </h2>

      {/* Details */}
      <div className="flex flex-col gap-0 rounded-xl border border-tg-hint/10 bg-tg-secondary shadow-sm">
        <DetailRow label={t('resources.detail.type')} value={t(`resources.filter.${item.resource_type}`)} />
        <DetailRow label={t('resources.detail.source')} value={item.source} />
        <DetailRow label={t('resources.detail.filePath')} value={item.file_path} />
        <DetailRow
          label={t('resources.detail.discoveredAt')}
          value={new Date(item.discovered_at).toLocaleString()}
        />
      </div>

      {/* Metadata */}
      {Object.keys(item.metadata).length > 0 && (
        <div className="flex flex-col gap-0 rounded-xl border border-tg-hint/10 bg-tg-secondary shadow-sm">
          {Object.entries(item.metadata).map(([key, value]) => (
            <DetailRow key={key} label={key} value={String(value)} />
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onUseInGeneration(item)}
          className="flex-1 rounded-xl bg-gradient-to-br from-tg-button to-tg-button/80 py-2.5 text-sm font-semibold text-tg-button-text shadow-lg shadow-tg-button/20 transition-all duration-200 active:scale-[0.98]"
        >
          {t('resources.useInGeneration')}
        </button>
      </div>
    </div>
  )
}

function DetailRow({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex justify-between gap-2 border-b border-tg-hint/10 px-3 py-2.5 last:border-b-0">
      <span className="shrink-0 text-xs font-medium text-tg-hint">
        {label}
      </span>
      <span className="max-w-[60%] break-words text-right text-xs text-tg-text">
        {value}
      </span>
    </div>
  )
}
